import { FC, ReactNode, MouseEvent, useCallback, useRef, useEffect, FocusEvent, PropsWithChildren } from "react";
import classNames from "classnames";
import { BaseProps } from "../../../BaseProps";
import { getBasePropsFromHtmlProps } from "../../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../../utilities/getHtmlPropsFromBaseProps";
import { Body, Bold, Regular, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Icon, TooltipStyle } from "capitalrx-components-2.0/lib/general";
import styles from "./styles.module.scss";

/**
 * The `Label` component properties.
 */
export interface LabelProps extends BaseProps {
  /**
   * The ID of the component the label is for.
   */
  readonly forId?: string;

  /**
   * The size of the label text. If not specified defaults to `"mini".
   */
  readonly size?: "mini" | "small" | "medium" | "inherit";

  /**
   * Indicates the appearance of the label. If not specified defaults to "semibold".
   */
  readonly appearance?: "bold" | "semibold" | "regular";

  /**
   * Indicates whether the input the label is for is required.
   */
  readonly required?: boolean;

  /**
   * The tooltip for the element.
   */
  readonly tooltip?: ReactNode;
}

/**
 * An input component label.
 */
export const Label: FC<PropsWithChildren<LabelProps>> = (props) => {
  const { forId, size = "mini", appearance = "semibold", required = false, tooltip, children } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Label, "label"],
    testIdPrefix: "label",
  });

  const forIdRef = useRef<string | undefined>(forId);
  useEffect(() => {
    forIdRef.current = forId;
  }, [forId]);

  const focusHandler = useCallback((ev: FocusEvent) => {
    ev.preventDefault();
  }, []);

  const mouseDownHandler = useCallback((ev: MouseEvent) => {
    const { current: forId } = forIdRef;
    const { button } = ev;

    if (button !== 0) return;
    if (forId === undefined) return;

    const target = document.getElementById(forId);
    target?.focus();

    ev.preventDefault();
  }, []);

  const mouseEnterHandler = useCallback(() => {
    const { current: forId } = forIdRef;
    if (forId === undefined) return;

    const target = document.getElementById(forId);
    const event = new CustomEvent("cust_labelMouseEnter", { bubbles: true, cancelable: true });
    target?.dispatchEvent(event);
  }, []);

  const mouseLeaveHandler = useCallback(() => {
    const { current: forId } = forIdRef;
    if (forId === undefined) return;

    const target = document.getElementById(forId);
    const event = new CustomEvent("cust_labelMouseExit", { bubbles: true, cancelable: true });
    target?.dispatchEvent(event);
  }, []);

  return (
    <Body {...getBasePropsFromHtmlProps(baseHtmlProps)} inline size={size}>
      <label
        id={forId === undefined ? undefined : `${forId}-label`}
        htmlFor={forId}
        tabIndex={-1}
        onFocus={focusHandler}
        onMouseDown={mouseDownHandler}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        <Shelf inline spacing="mini">
          {appearance === "regular" && (
            <Regular>
              {children}
              {required && children && <>&nbsp;<Semibold>*</Semibold></>}
            </Regular>
          )}
          {appearance === "semibold" && (
            <Semibold>
              {children}
              {required && children && <>&nbsp;*</>}
            </Semibold>
          )}
          {appearance === "bold" && (
            <Bold>
              {children}
              {required && children && <>&nbsp;<Semibold>*</Semibold></>}
            </Bold>
          )}
          {tooltip && (
            <TooltipStyle alignment="top">
              <Icon className={classNames(styles.Icon, "tooltip-icon")} name="info-circle" size="small" color="body" tooltip={tooltip} />
            </TooltipStyle>
          )}
          <Shelf.Spacer />
        </Shelf>
      </label>
    </Body>
  );
};
